import { inject, Injectable, OnDestroy } from "@angular/core";
import { BrowserStorageService } from "@nx-superprep/backend/config";
import { log } from "@nx-superprep/utils"
import { Howl } from "howler";

@Injectable({ providedIn: 'root' })
export class GameplayService implements OnDestroy {
  private _effects: Howl | undefined = undefined;
  private readonly maxRetryAttempts = 3;
  private retryCount = 0;
  private retryDelay = 1000; // 1 second delay between retries
  readonly browserStorage = inject(BrowserStorageService);

  /**
   * Initializes the sound effects with retry logic.
   */
  initializeSoundEffects(): void {
    // Ensure any existing instance is cleaned up before creating a new one
    this.cleanup();
    this.retryCount = 0; // Reset retry counter

    this._tryInitialize();
  }

  /**
   * Internal method to attempt initialization with retry logic
   */
  private _tryInitialize(): void {
    try {
      this._effects = new Howl({
        src: ['/assets/gameplay/effects.mp3'],
        volume: (Number(this.browserStorage.getVolume()) || 1) * 0.3,
        html5: true,
        format: ['webm', 'mp3', 'aac'],
        preload: true,
        sprite: {
          correct: [0, 810],
          incorrect: [2000, 810],
          jingle: [4000, 1542],
        },
        onloaderror: (id, error) => {
          log.error('Failed to load sound effects:', error);
          this._handleInitializationError();
        },
        onload: () => {
          this.retryCount = 0; // Reset retry counter on success
          log.debug('Sound effects loaded successfully');
        }
      });
    } catch (error) {
      this._handleInitializationError(error);
    }
  }

  /**
   * Handles initialization errors and retries if attempts remain
   */
  private _handleInitializationError(error?: unknown): void {
    this._effects = undefined;

    if (error) {
      log.error('Error initializing sound effects:', error);
    }

    if (this.retryCount < this.maxRetryAttempts) {
      this.retryCount++;
      log.debug(`Retrying sound effects initialization (attempt ${this.retryCount}/${this.maxRetryAttempts})`);
      setTimeout(() => this._tryInitialize(), this.retryDelay);
    } else {
      log.warn('Max retry attempts reached for sound effects initialization');
    }
  }

  /**
   * Plays a sound effect. Attempts to initialize if not already initialized.
   * @param effect - The sound effect to play ('correct' or 'incorrect').
   */
  playSoundEffect(effect: 'correct' | 'incorrect'): void {
    if (!this._effects) {
      log.debug('Sound effects not initialized. Attempting initialization...');
      this.initializeSoundEffects();

      // If still not initialized after retry, return
      if (!this._effects) {
        log.warn('Unable to play sound effect - initialization failed');
        return;
      }
    }

    try {
      this._effects.play(effect);
    } catch (error) {
      log.error('Error playing sound effect:', error);
    }
  }

  /**
   * Cleans up resources when the service is destroyed.
   */
  cleanup(): void {
    if (this._effects) {
      this._effects.unload(); // Unload the Howl instance
      this._effects = undefined;
    }
  }

  /**
   * Angular lifecycle hook to clean up resources when the service is destroyed.
   */
  ngOnDestroy(): void {
    this.cleanup();
  }

  setVolume(volume: number): void {
    if (this._effects) {
      this._effects.volume(volume * 0.3);
    }
  }

  isInitialized(): boolean {
    return !!this._effects;
  }

  lockOrientation(orientation: 'portrait' | 'landscape'): void {
    // Implementation for locking orientation
  }
}
